import "../../App.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import BackOnly from "../../components/BackOnly";
import ContactPopup from "../../components/ContactPopup";
import AffiliateBooking from "../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../components/affiliate/AffiliateTrip";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Nevada ALWAYS goes back to USA map
export const backRoute = "/map/usa";

export default function Nevada() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly
  useEffect(() => {
    navigate(".", { state: { backRoute } });
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Nevada</h1>

        <section className="travel-review">
          {/* LEFT COLUMN */}
          <div className="travel-text">
            <h2>🎲 Discover Nevada</h2>

            <p>
              Nevada, known as the “Silver State,” is a land of contrasts — from the vibrant
              lights of Las Vegas to the serene beauty of its desert landscapes and mountain
              ranges. Whether you're seeking world‑class entertainment, outdoor adventures, or
              historical sites, Nevada has something for every traveler.
            </p>

            <p>
              It’s also home to the famous Area 51, a top‑secret military base that has sparked
              countless conspiracy theories and UFO sightings. In this section, I focus on Las
              Vegas and the surrounding areas — places I’ve personally explored and enjoyed.
            </p>

            <p>
              Nevada is a desert state, so be prepared for hot temperatures, especially in the
              summer months. Stay hydrated, wear sunscreen, and keep in mind that some areas can
              be remote — plan accordingly.
            </p>

            <h3>🚗 Getting There</h3>

            <p>
              The most common way to reach Nevada is by flying into Las Vegas McCarran
              International Airport (LAS). The airport is well‑connected to major U.S. cities and
              international destinations. From there, you can rent a car or take a shuttle to your
              hotel.
            </p>

            <p>
              Some travelers also drive to Nevada, especially from nearby states like California
              or Arizona. I drove from Colorado — a scenic 12‑hour journey through the Rocky
              Mountains and the desert landscapes of Utah and Nevada.
            </p>

            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Fremont Street Experience:</strong> A must‑see in downtown Las Vegas, this
                  pedestrian mall is crowned by a massive LED canopy that bursts into hourly light
                  shows. The energy is electric — with street performers, live music, and endless food
                  and drink options.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=36.17051094761079,-115.14357614124233"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Fremont Street Experience
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Hoover Dam:</strong> One of America’s greatest engineering feats, Hoover Dam
                  straddles the border between Nevada and Arizona. Built between 1931 and 1936, this
                  concrete gravity‑arch dam towers 726 feet high and creates Lake Mead — the largest
                  reservoir in the U.S. by volume.
                  <br />
                  It generates hydroelectric power for over a million people and supplies water to
                  cities like Los Angeles and Phoenix. The dam offers stunning views, art deco
                  architecture, and guided tours of its massive turbines.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=36.0156,-114.7377"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Hoover Dam
                  </a>
                </div>
              </li>
            </ul>

            <h3>🌄 Other Hidden Gems – Nevada</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Ice Box Canyon (Red Rock Canyon):</strong> A shaded desert trail leading
                  into a cool, narrow box canyon with seasonal waterfalls and dramatic rock walls.<br />
                  <a
                    href="https://www.google.com/maps?q=36.155278,-115.473333"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Ice Box Canyon Trailhead
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Calico Basin:</strong> A lesser‑known area near Red Rock Canyon with vivid
                  red sandstone, quiet trails, and striking desert flora.<br />
                  <a
                    href="https://www.google.com/maps?q=36.155,-115.423"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Calico Basin
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Corn Creek (Desert National Wildlife Refuge):</strong> A lush oasis with
                  springs, cottonwood trees, and historic remnants — great for birdwatching and quiet
                  walks.<br />
                  <a
                    href="https://www.google.com/maps?q=36.4375,-115.326389"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Corn Creek
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>White Pinnacle Peak:</strong> A jagged limestone summit offering panoramic
                  views and solitude. A rewarding climb for those seeking silence and scale.<br />
                  <a
                    href="https://www.google.com/maps?q=36.159167,-115.498056"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 White Pinnacle Peak
                  </a>
                </div>
              </li>
            </ul>

            <h3>🛏️ Places to Stay – Las Vegas</h3>

            <p>
              I stayed at the <strong>Sahara Las Vegas</strong> — clean, modern, and surprisingly
              quiet given its location near the Strip. I also stayed at{" "}
              <strong>SpringHill Suites by Marriott</strong>, which offered peace, spacious rooms,
              and solid amenities.
            </p>

            <p>
              Las Vegas has no shortage of places to stay, but a few consistently rise to the top
              for their service, location, and overall experience.
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Bellagio:</strong> Famous for its dancing fountains and romantic ambiance.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Wynn Las Vegas:</strong> A high‑end favorite with upscale dining and a serene
                  spa.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>The Venetian Resort:</strong> Opulent suites, canals, and world‑class
                  restaurants.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Caesars Palace:</strong> Known for its lively pool scene and iconic
                  architecture.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Park MGM:</strong> A solid value pick with smoke‑free rooms and modern
                  design.
                </div>
              </li>
            </ul>

           <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                       <div className="affiliate-block">
                         <AffiliateExpedia />
                         <AffiliateBooking />
                         <AffiliateTrip />
                       </div>

            <h3>🎥 My Travel Videos</h3>

            <p>Here are some videos I took from Nevada. Hope you enjoy them!</p>

            <p>
              <a
                href="https://youtu.be/wwerqmDMVhA?si=id_z4PGdoWx2R9hh"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fremont Street
              </a>
              <br />
              <a
                href="https://youtu.be/MwNqijeaOo0?si=KmwRC-HMgfMh2GA_"
                target="_blank"
                rel="noopener noreferrer"
              >
                Hoover Dam
              </a>
              <br />
              <a
                href="https://youtu.be/CP1u7hajBBg?si=hgKr_FsAOO3qLr4G"
                target="_blank"
                rel="noopener noreferrer"
              >
                Las Vegas Street
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/wwerqmDMVhA?si=id_z4PGdoWx2R9hh"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/nevada/fremontstreet.jpg"
                alt="Fremont Street Video"
              />
            </a>

            <a
              href="https://youtu.be/MwNqijeaOo0?si=N4iprF2fkTdf1P5t"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img src="/images/usa/nevada/bridge.jpg" alt="Hoover Dam Video" />
            </a>

            <img src="/images/usa/nevada/paris.jpg" alt="Paris" />
            <img src="/images/usa/nevada/casinos.jpg" alt="Casinos" />
            <img src="/images/usa/nevada/streetinnight.jpg" alt="Street at night" />
            <img src="/images/usa/nevada/street.jpg" alt="Street" />
            <img src="/images/usa/nevada/venecia.jpg" alt="Venecia" />
            <img src="/images/usa/nevada/moreofvenetia.jpg" alt="More of Venetia" />
            <img src="/images/usa/nevada/hoover.jpg" alt="Hoover Dam" />
            <img src="/images/usa/nevada/hotel.jpg" alt="Hotel" />
            <img src="/images/usa/nevada/pool.jpg" alt="Pool" />
            <img src="/images/usa/nevada/piramids.jpg" alt="Pyramids" />
            <img src="/images/usa/nevada/mandalay.jpg" alt="Mandalay" />
            <img src="/images/usa/nevada/emptystreet.jpg" alt="Empty Street" />
            <img src="/images/usa/nevada/nyinvegas.jpg" alt="New York in Vegas" />
            <img src="/images/usa/nevada/torre.jpg" alt="Tower" />
            <img src="/images/usa/nevada/ship.jpg" alt="Ship" />
          </div>
        </section>
      </div>

      {/* EMAIL BUTTON */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      {/* POPUP */}
      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
